/**
 * Selector that returns all the completed questions.
 *
 * This is decided by looping through the questions and filtering by those which
 * have answers associated with it.
 *
 * I've also made it so that it filters out any repeating subQuestions with the
 * same parent, so that the number completed and number total don't mess up
 * (eg, the completed is higher than the number of questions).
 *
 * I feel this may be open to bugs and it seems kind of wrong, so maybe one to
 * revisit in the future.
 */

import { createSelector } from 'reselect';
import { uniqBy } from 'es-toolkit';

import normalisePath from '/business/shared/functions/normalisePath';
import type { QuestionTreeItem } from '/business/shared/functions/types';
import type { StructuredAnswer } from '/business/shared/state/reducers/basket';

import getQuestionsSelector from './getQuestionsSelector';
import getIncludesNoData from './getIncludesNoData';
import getApplicationStructuredDataSelector from '/business/shared/state/selectors/application/getApplicationStructuredDataSelector';

const questionsCompletedSelector = createSelector(
  [getQuestionsSelector, getIncludesNoData, getApplicationStructuredDataSelector],
  (questions, includesNoData, structuredData) => {
    const all = questions?.filter((question: QuestionTreeItem) => {
      const relevantLine = structuredData?.find((structuredLine: StructuredAnswer) => {
        if (!question?.path) {
          return false;
        }
        return structuredLine.key === normalisePath({ question });
      });
      if (relevantLine) {
        return relevantLine.hasAnswers;
      }
      return false;
    });

    const traverseToQuestionDepth = (question: QuestionTreeItem): QuestionTreeItem | null => {
      if ((question.depth || 0) <= 2) {
        return question;
      }
      if (!question.parent) {
        return null;
      }
      return traverseToQuestionDepth(question.parent);
    };

    return uniqBy(all || [], ({ type, parent, path }: QuestionTreeItem) => {
      if (type === 'question' || !parent?.id) {
        return Array.isArray(path) ? path.join('-') : path || '';
      }
      const rootParent = traverseToQuestionDepth(parent);
      if (rootParent?.id && type) {
        return type + rootParent.id;
      }
      return null;
    });
  },
);

export default questionsCompletedSelector;
